import { usePermissionMap } from '../permissions'
import { type DayGroupedEvents, type PartialCalendarEvent } from './types'
import { isTeamId, type TeamId, type CalendarEventData, type CalendarEventDataWithDetails } from '@terros/common'
import { DateTime, Interval } from 'luxon'

export function useEventManageTeams(): TeamId[] {
  const entityIds = usePermissionMap({ permissionType: 'event:manage' })
  return entityIds.filter(isTeamId)
}

export const splitEventsByDay = (events: CalendarEventData[], days: DateTime[]): DayGroupedEvents => {
  return days.reduce<DayGroupedEvents>((acc, day) => {
    const dayEvents = events.filter((event) => DateTime.fromMillis(event.eventDate).hasSame(day, 'day'))
    const iso = day.toISO()
    if (iso === null) throw new Error('Day toISO is null')
    acc[iso] = getOverlappingGroups(dayEvents)
    return acc
  }, {})
}

export const getOverlappingGroups = <T extends PartialCalendarEvent>(events: T[]): T[][] => {
  const sortedEvents = [...events].sort((a, b) => a.eventDate - b.eventDate)
  const groups: T[][] = []

  for (const event of sortedEvents) {
    const eventInterval = Interval.fromDateTimes(
      DateTime.fromMillis(event.eventDate),
      DateTime.fromMillis(event.eventDate).plus({ minutes: event.duration })
    )

    let foundGroup = false
    for (const group of groups) {
      const overlapsWithGroup = group.some((groupEvent) => {
        const groupEventInterval = Interval.fromDateTimes(
          DateTime.fromMillis(groupEvent.eventDate),
          DateTime.fromMillis(groupEvent.eventDate).plus({ minutes: groupEvent.duration })
        )
        return eventInterval.overlaps(groupEventInterval)
      })

      if (overlapsWithGroup) {
        group.push(event)
        foundGroup = true
        break
      }
    }

    if (!foundGroup) {
      groups.push([event])
    }
  }

  return groups
}

type EventPosition = {
  top: number
  height: number
  width: number
  left: number
}

export const calcEventPosition = (
  event: CalendarEventDataWithDetails,
  overlappingEvents: CalendarEventDataWithDetails[],
  eventIndex: number
): EventPosition => {
  const startTime = DateTime.fromMillis(event.eventDate)
  const endTime = startTime.plus({ minutes: event.duration })

  const top = startTime.hour * 60 + startTime.minute
  const height = endTime.diff(startTime, 'minutes').minutes
  const { column, totalColumns } = getPackedEventColumn(event, overlappingEvents, eventIndex)

  const width = 100 / totalColumns
  const left = width * column

  return { top, height, width, left }
}

const getPackedEventColumn = (
  event: CalendarEventDataWithDetails,
  overlappingEvents: CalendarEventDataWithDetails[],
  eventIndex: number
): { column: number; totalColumns: number } => {
  const positioned = overlappingEvents
    .map((item) => {
      const startTime = DateTime.fromMillis(item.eventDate)
      const start = startTime.hour * 60 + startTime.minute
      return { item, start, end: start + item.duration }
    })
    .sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start
      if (a.end !== b.end) return a.end - b.end
      return a.item.eventId.localeCompare(b.item.eventId)
    })

  const columnEnds: number[] = []
  const columns = new Map<string, number>()

  for (const entry of positioned) {
    const column = columnEnds.findIndex((end) => end <= entry.start)
    const nextColumn = column === -1 ? columnEnds.length : column
    columnEnds[nextColumn] = entry.end
    columns.set(entry.item.eventId, nextColumn)
  }

  return {
    column: columns.get(event.eventId) ?? eventIndex,
    totalColumns: Math.max(columnEnds.length, 1),
  }
}
